import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useId } from 'react';
import { useValidateFormRequiredProps, getCommonClassNameDefault } from '@skatteetaten/ds-core-utils';
import { ErrorMessage } from '../ErrorMessage/index.esm.js';
import { Fieldset } from '../Fieldset/index.esm.js';
import { Radio } from '../Radio/index.esm.js';
import { RadioGroupContext } from '../RadioGroupContext/index.esm.js';
import '@skatteetaten/ds-icons';
import '../Help/index.esm.js';
import 'react-i18next';
import '@skatteetaten/ds-buttons';
import './styles.css';


const getRadioGroupVariantDefault = ()=>'standard';

var styles = {
    "radioGroupItemContainer": "RadioGroup-module_radioGroupItemContainer__xufMt",
    "radioGroupItemContainer_horizontal": "RadioGroup-module_radioGroupItemContainer_horizontal__iRDTj",
    "errorMessage": "RadioGroup-module_errorMessage__w61EC"
};

const RadioGroup = /*#__PURE__*/ forwardRef(({ id, className = getCommonClassNameDefault(), classNames, lang, 'data-testid': dataTestId, description, errorMessage, helpSvgPath, helpText, legend, selectedValue, titleHelpSvg, variant = getRadioGroupVariantDefault(), defaultValue, disabled, form, name, required, hideLegend, showRequiredMark, shadowRootNode, onBlur: onBlurExternal, onChange: onChangeExternal, onHelpToggle, children }, ref)=>{
    useValidateFormRequiredProps({
        required,
        showRequiredMark
    });
    const errorId = `radioErrorId-${useId()}`;
    const uniqueNameId = `radioInputName-${useId()}`;
    const nameId = name ?? uniqueNameId;
    const variantClassName = variant === 'horizontal' ? `${styles.radioGroupItemContainer_horizontal}` : '';
    const radioGroupItemContainer = `${styles.radioGroupItemContainer} ${variantClassName}`.trim();
    const handleBlur = (event)=>{
        const blurredRadio = event.target;
        const clickedElement = event.relatedTarget;
        let clickedRadioName;
        if (clickedElement instanceof HTMLInputElement && clickedElement.type === 'radio') {
            clickedRadioName = clickedElement.name;
        } else if (clickedElement instanceof HTMLLabelElement) {
            let inputElement;
            if (shadowRootNode) {
                inputElement = shadowRootNode.getElementById(clickedElement.htmlFor);
            } else {
                inputElement = document.getElementById(clickedElement.htmlFor);
            }
            clickedRadioName = inputElement.name;
        }
        if (clickedRadioName !== blurredRadio.name && onBlurExternal) {
            onBlurExternal?.(event);
        }
    };
    const handleChange = (event)=>{
        onChangeExternal?.(event);
    };
    return /*#__PURE__*/ jsxs(Fieldset, {
        ref: ref,
        id: id,
        className: className,
        classNames: classNames,
        lang: lang,
        "data-testid": dataTestId,
        disabled: disabled,
        form: form,
        legend: legend,
        hideLegend: hideLegend,
        showRequiredMark: showRequiredMark,
        description: description,
        helpSvgPath: helpSvgPath,
        helpText: helpText,
        titleHelpSvg: titleHelpSvg,
        hasSpacing: true,
        onHelpToggle: onHelpToggle,
        children: [
            /*#__PURE__*/ jsx("div", {
                className: radioGroupItemContainer,
                children: /*#__PURE__*/ jsx(RadioGroupContext.Provider, {
                    value: {
                        defaultValue,
                        errorId: errorMessage ? errorId : '',
                        selectedValue,
                        name: nameId,
                        hasError: !!errorMessage || undefined,
                        required,
                        onChange: handleChange,
                        onBlur: handleBlur
                    },
                    children: children
                })
            }),
            /*#__PURE__*/ jsx(ErrorMessage, {
                className: `${styles.errorMessage} ${classNames?.errorMessage ?? ''}`.trim(),
                id: errorId,
                showError: !!errorMessage,
                children: errorMessage
            })
        ]
    });
});
RadioGroup.displayName = 'RadioGroup';
RadioGroup.Radio = Radio;

export { RadioGroup, getRadioGroupVariantDefault };
